/**
 * Department to Service Category Mapping
 * Matches backend/utils/departmentMapping.js
 * Maps department names to service categories they can access
 */

export const DEPARTMENT_SERVICES = {
  'Housekeeping Service': ['Room Service', 'Housekeeping', 'Housekeeping Service', 'Room Cleaning', 'Toiletries & Supplies', 'toiletries & supplies'],
  'Maintenance Assistance': ['Room Service', 'Maintenance', 'Maintenance Assistance'],
  'IT Support': ['Room Service', 'IT Support', 'it_support', 'IT'],
  'Front Office': ['Room Service', 'Front Office', 'front_office', 'Concierge', 'Transport Arrangements'],
  'Medical Emergency': ['Medical Emergency', 'Medical'],
  'Security Assistance': ['Security Assistance', 'Security'],
  'SPA Appointment': ['Spa', 'SPA Appointment', 'SPA Service'],
  'Yoga & Wellness': ['Yoga', 'Yoga & Wellness', 'Yoga Session'],
  'Salon': ['Salon']
};

export const SERVICE_REQUEST_ACTIVITIES = [
  'Housekeeping Service',
  'Maintenance Assistance',
  'IT Support',
  'Front Office',
  'Medical Emergency',
  'Security Assistance',
  'SPA Appointment',
  'Yoga & Wellness',
  'Salon'
];

export const LEGACY_DEPARTMENT_ALIASES = {
  Housekeeping: 'Housekeeping Service',
  Medical: 'Medical Emergency',
  Security: 'Security Assistance',
  Spa: 'SPA Appointment',
  Yoga: 'Yoga & Wellness',
  Salon: 'Salon'
};

/**
 * Get service categories accessible by a department
 * @param {string} department - Department name
 * @returns {string[]} Array of service categories
 */
export function getServicesForDepartment(department) {
  if (!department) return [];

  const normalizedDepartment = normalizeDepartment(department);
  
  // Try exact match first
  if (DEPARTMENT_SERVICES[normalizedDepartment]) {
    return DEPARTMENT_SERVICES[normalizedDepartment];
  }
  
  // Try case-insensitive match
  const deptKey = Object.keys(DEPARTMENT_SERVICES).find(
    key => key.toLowerCase().trim() === normalizedDepartment.toLowerCase().trim()
  );
  
  if (deptKey) {
    return DEPARTMENT_SERVICES[deptKey];
  }
  
  return [];
}

/**
 * Check if a department can access a service category
 * @param {string} department - Department name
 * @param {string} category - Service category
 * @returns {boolean}
 */
export function canAccessService(department, category) {
  if (!department || !category) return false;
  const services = getServicesForDepartment(department);
  return services.includes(category);
}

/**
 * Get all available departments
 * @returns {string[]}
 */
export function getAllDepartments() {
  return Object.keys(DEPARTMENT_SERVICES);
}

export function normalizeDepartment(department) {
  if (!department) return department;
  return LEGACY_DEPARTMENT_ALIASES[department] || department;
}

export function normalizeCategory(category) {
  if (!category) return category;

  const categoryMap = {
    'front_office': 'Front Office',
    'front office': 'Front Office',
    'it_support': 'IT Support',
    'it support': 'IT Support',
    'Room Cleaning': 'Room Service',
    'Room cleaning': 'Room Service',
    'room cleaning': 'Room Service',
    'Toiletries & Supplies': 'Room Service',
    'Toiletries and Supplies': 'Room Service',
    'toiletries & supplies': 'Room Service',
    Maintenance: 'Room Service',
    maintenance: 'Room Service',
    Medical: 'Medical Emergency',
    medical: 'Medical Emergency',
    Security: 'Security Assistance',
    security: 'Security Assistance',
    'SPA Appointment': 'Spa',
    'Yoga & Wellness': 'Yoga'
  };

  return categoryMap[category] || category;
}

export function getDepartmentForCategory(category) {
  if (!category) return 'Unmapped';

  const normalized = normalizeCategory(category);
  const directDepartmentMap = {
    'Housekeeping Service': 'Housekeeping Service',
    'Maintenance Assistance': 'Maintenance Assistance',
    'IT Support': 'IT Support',
    'Front Office': 'Front Office',
    'Medical Emergency': 'Medical Emergency',
    'Security Assistance': 'Security Assistance',
    'Transport Arrangements': 'Front Office',
    'SPA Appointment': 'SPA Appointment',
    'Yoga & Wellness': 'Yoga & Wellness',
    'Salon': 'Salon',
    'Room Service': 'Housekeeping Service',
    Maintenance: 'Maintenance Assistance',
    Concierge: 'Front Office',
    'Contact Concierge': 'Front Office',
    Spa: 'SPA Appointment',
    Yoga: 'Yoga & Wellness',
    Transport: 'Front Office',
    transport: 'Front Office'
  };

  if (directDepartmentMap[category]) return directDepartmentMap[category];
  if (directDepartmentMap[normalized]) return directDepartmentMap[normalized];

  const mappedDepartment = Object.keys(DEPARTMENT_SERVICES).find((department) =>
    (DEPARTMENT_SERVICES[department] || []).includes(normalized)
  );

  return mappedDepartment || 'Unmapped';
}

export function normalizeServiceRequestActivity(activity) {
  if (!activity) return null;

  const normalized = String(activity).trim().toLowerCase().replace(/[-_]+/g, ' ');
  const activityAliases = {
    'housekeeping service': 'Housekeeping Service',
    housekeeping: 'Housekeeping Service',
    'room service': 'Housekeeping Service',
    'room cleaning': 'Housekeeping Service',
    'toiletries & supplies': 'Housekeeping Service',
    'toiletries and supplies': 'Housekeeping Service',
    toiletries: 'Housekeeping Service',
    'maintenance assistance': 'Maintenance Assistance',
    maintenance: 'Maintenance Assistance',
    'it support': 'IT Support',
    it: 'IT Support',
    'front office': 'Front Office',
    concierge: 'Front Office',
    'recreation booking': 'Front Office',
    'recreation booking enquiry': 'Front Office',
    'recreation activities': 'Front Office',
    recreation: 'Front Office',
    'restaurant booking': 'Front Office',
    'restaurant booking enquiry': 'Front Office',
    'restaurant table booking': 'Front Office',
    restaurants: 'Front Office',
    restaurant: 'Front Office',
    dining: 'Front Office',
    'medical emergency': 'Medical Emergency',
    medical: 'Medical Emergency',
    'security assistance': 'Security Assistance',
    security: 'Security Assistance',
    'transport arrangements': 'Front Office',
    transport: 'Front Office',
    'spa appointment': 'SPA Appointment',
    spa: 'SPA Appointment',
    'yoga & wellness': 'Yoga & Wellness',
    'yoga and wellness': 'Yoga & Wellness',
    'yoga wellness': 'Yoga & Wellness',
    yoga: 'Yoga & Wellness',
    salon: 'Salon'
  };

  if (activityAliases[normalized]) return activityAliases[normalized];
  if (activityAliases[String(activity).trim().toLowerCase()]) return activityAliases[String(activity).trim().toLowerCase()];

  return activity;
}

export function getAllServiceRequestActivities() {
  return [...SERVICE_REQUEST_ACTIVITIES];
}

export function getDefaultActivitiesForDepartment(department) {
  const normalizedDepartment = normalizeDepartment(department);

  if (normalizedDepartment === 'Security Assistance') {
    return ['Security Assistance'];
  }

  if (normalizedDepartment === 'Medical Emergency') {
    return ['Medical Emergency'];
  }

  if (SERVICE_REQUEST_ACTIVITIES.includes(normalizedDepartment)) {
    return [normalizedDepartment];
  }

  return [];
}

const DASHBOARD_MANAGED_BOOKING_CATEGORIES = new Set(['Yoga', 'Spa', 'Salon']);

export function isDashboardManagedBookingCategory(category) {
  if (!category) return false;
  return DASHBOARD_MANAGED_BOOKING_CATEGORIES.has(normalizeCategory(category));
}

/** Matches department_staff.department for preset dropdown */
export function getDepartmentStaffDepartmentForBookingCategory(category) {
  const n = normalizeCategory(category);
  if (n === 'Yoga') return 'Yoga & Wellness';
  if (n === 'Spa') return 'SPA Appointment';
  if (n === 'Salon') return 'Salon';
  return null;
}

export function getBookingActivityGuestLabel(category) {
  const n = normalizeCategory(category);
  if (n === 'Yoga') return 'Yoga';
  if (n === 'Spa') return 'Spa';
  if (n === 'Salon') return 'Salon';
  return n || 'Activity';
}

export function getCategoryAliases(category) {
  if (!category || category === 'all') return [];
  const normalized = normalizeServiceRequestActivity(category) || category;

  const aliasMap = {
    'Housekeeping Service': [
      'Housekeeping Service', 'housekeeping service', 'Housekeeping', 'housekeeping',
      'Room Service', 'room service', 'Room Cleaning', 'room cleaning',
      'Toiletries & Supplies', 'toiletries & supplies', 'Toiletries and Supplies', 'toiletries and supplies',
      'Toiletries', 'toiletries', 'Room Cleaning Service'
    ],
    'Maintenance Assistance': [
      'Maintenance Assistance', 'maintenance assistance', 'Maintenance', 'maintenance',
      'Maintenance Request'
    ],
    'IT Support': [
      'IT Support', 'it support', 'it_support', 'IT', 'it', 'IT Assistance'
    ],
    'Front Office': [
      'Front Office', 'front office', 'front_office', 'Concierge', 'concierge',
      'recreation booking', 'recreation booking enquiry', 'recreation activities', 'recreation',
      'restaurant booking', 'restaurant booking enquiry', 'restaurant table booking', 'restaurants', 'restaurant', 'dining',
      'Transport Arrangements', 'transport arrangements', 'Transport', 'transport'
    ],
    'Medical Emergency': [
      'Medical Emergency', 'medical emergency', 'Medical', 'medical', 'Doctor Consultation'
    ],
    'Security Assistance': [
      'Security Assistance', 'security assistance', 'Security', 'security'
    ],
    'SPA Appointment': [
      'SPA Appointment', 'spa appointment', 'Spa', 'spa', 'SPA', 'Spa Appointment', 'spa_appointment',
      'SPA Service', 'Spa Service'
    ],
    'Yoga & Wellness': [
      'Yoga & Wellness', 'yoga & wellness', 'Yoga and Wellness', 'yoga and wellness',
      'Yoga', 'yoga', 'Yoga Wellness', 'yoga wellness', 'yoga_wellness', 'Yoga Session'
    ],
    'Salon': [
      'Salon', 'salon', 'Salon Appointment'
    ]
  };

  const list = aliasMap[normalized] || aliasMap[category] || [category];
  return Array.from(new Set([category, normalized, ...list]));
}
